import {
  translateVehicleModelFuel,
  translateVehicleModelType,
  VehicleModelWithInsurancesForD2CDto,
} from '@orcar/common';
import {
  getModelYearText,
  getPrimaryColor,
  getVehicleDiscountRate,
  getVehicleModelDisplayPrice,
  JEJURO,
} from '@orcar/common-d2c';
import styled, { css } from 'styled-components';
import { optimizedImageUrl } from '@/utils/image';

type SearchResultCardProps = {
  vehicleModel: VehicleModelWithInsurancesForD2CDto;
  unavailable?: boolean;
  isMobile?: boolean;
  /** 예약하기(데스크탑) 또는 카드 탭 → 해당 차량으로 상세/결제 진행 */
  onReserve: (vehicleModel: VehicleModelWithInsurancesForD2CDto) => void;
};

/**
 * 검색 결과 차량 카드.
 * 상단: 차량 정보(좌) · 차량 이미지(우). 이어서 대여사업자 · 보유 옵션 태그 · 차량료 박스.
 * 데스크탑은 우하단 예약 버튼, 모바일은 카드 전체 탭으로 진행한다.
 */
const SearchResultCard = ({
  vehicleModel,
  unavailable,
  isMobile,
  onReserve,
}: SearchResultCardProps) => {
  const vehiclePrice = getVehicleModelDisplayPrice(vehicleModel);
  const discountRate = getVehicleDiscountRate(
    vehiclePrice,
    vehicleModel.referencePrice,
  );

  const specs = [
    translateVehicleModelType(vehicleModel.type),
    `${vehicleModel.seat}인승`,
    translateVehicleModelFuel(vehicleModel.fuel),
    getModelYearText(vehicleModel.minModelYear, vehicleModel.maxModelYear),
  ].filter(Boolean);

  // 고시제 대응: 제주로 자사몰은 갤럭시렌터카(위·수탁)가 대여사업자이므로
  // 모든 차량 카드에 '갤럭시 렌터카'를 대여사업자로 표기한다.
  const branchName = '갤럭시 렌터카';

  const opt = vehicleModel.modelOption;
  // 보유 옵션만 간결하게 노출(어수선함 방지). 실제 modelOption 필드 기반.
  const ownedFeatures = [
    { label: '블랙박스', on: !!opt?.blackBox },
    {
      label: '내비게이션',
      on: !!(opt?.navigation1 || opt?.navigation2 || opt?.navigation3),
    },
    { label: '후방카메라', on: !!opt?.parkingCamera },
    { label: '후방센서', on: !!opt?.parkingSensor },
    { label: '열선시트', on: !!opt?.seatHeater },
    { label: '통풍시트', on: !!(opt?.ventilatedSeat1 || opt?.ventilatedSeat2) },
  ]
    .filter((f) => f.on)
    .map((f) => f.label);

  return (
    <Card
      isMobile={isMobile}
      unavailable={unavailable}
      clickable={!unavailable}
      onClick={!unavailable ? () => onReserve(vehicleModel) : undefined}
    >
      <Head>
        <Info>
          <Name isMobile={isMobile}>
            {vehicleModel.title || vehicleModel.name}
          </Name>
          <SpecLine>{specs.join(' · ')}</SpecLine>
          {vehicleModel.driverAge ? (
            <SubLine>만 {vehicleModel.driverAge}세 이상 대여 가능</SubLine>
          ) : null}
          <BranchLine>
            <BranchTag>대여사업자</BranchTag>
            <BranchName>{branchName}</BranchName>
          </BranchLine>
        </Info>

        <Thumb isMobile={isMobile}>
          {unavailable && <SoldOut>예약 마감</SoldOut>}
          <img
            src={optimizedImageUrl(vehicleModel.imageUrls?.[0])}
            alt={vehicleModel.name}
            loading='lazy'
          />
        </Thumb>
      </Head>

      {ownedFeatures.length > 0 && (
        <Tags>
          {ownedFeatures.map((label) => (
            <Tag key={label}>{label}</Tag>
          ))}
        </Tags>
      )}

      <Bottom isMobile={isMobile}>
        {!unavailable && (
          <Price>
            <PriceLabel>차량료</PriceLabel>
            {discountRate !== null && vehicleModel.referencePrice ? (
              <PriceSub>
                <Strike>
                  {vehicleModel.referencePrice.toLocaleString()}원
                </Strike>
                <DiscountBadge>{discountRate}%</DiscountBadge>
              </PriceSub>
            ) : null}
            <PriceMain>
              {vehiclePrice != null ? (
                <>
                  {vehiclePrice.toLocaleString()}
                  <PriceUnit>원</PriceUnit>
                </>
              ) : (
                '요금 확인 중'
              )}
            </PriceMain>
          </Price>
        )}
        {!isMobile && (
          <ReserveButton
            type='button'
            disabled={unavailable}
            onClick={(e) => {
              e.stopPropagation();
              if (!unavailable) onReserve(vehicleModel);
            }}
          >
            {unavailable ? '예약 마감' : '예약하기'}
          </ReserveButton>
        )}
      </Bottom>
    </Card>
  );
};

export default SearchResultCard;

const Card = styled.div<{
  isMobile?: boolean;
  unavailable?: boolean;
  clickable?: boolean;
}>`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: ${({ isMobile }) => (isMobile ? '16px' : '20px 24px')};
  border: 1px solid ${({ theme }) => theme.COLOR.GREY_05};
  border-radius: 16px;
  background: ${({ theme }) => theme.COLOR.WHITE};
  box-shadow: 0 2px 10px rgba(15, 27, 36, 0.04);
  cursor: ${({ clickable }) => (clickable ? 'pointer' : 'default')};
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease;

  ${({ clickable, theme }) =>
    clickable &&
    css`
      &:hover {
        border-color: ${getPrimaryColor(JEJURO)}66;
        box-shadow: 0 6px 18px rgba(15, 27, 36, 0.08);
      }
    `}

  ${({ unavailable }) =>
    unavailable &&
    css`
      opacity: 0.55;
    `}
`;

const Head = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
`;

const Info = styled.div`
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 6px;
`;

const Name = styled.h3<{ isMobile?: boolean }>`
  margin: 0;
  ${({ isMobile, theme }) => (isMobile ? theme.FONT.H4 : theme.FONT.H3)};
  font-weight: 800;
  letter-spacing: -0.02em;
  word-break: keep-all;
`;

const SpecLine = styled.p`
  margin: 0;
  ${({ theme }) => theme.FONT.B2};
  color: ${({ theme }) => theme.COLOR.GREY_02};
`;

const SubLine = styled.p`
  margin: 0;
  ${({ theme }) => theme.FONT.B3};
  color: ${({ theme }) => theme.COLOR.GREY_03};
`;

const BranchLine = styled.p`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  margin: 2px 0 0;
  ${({ theme }) => theme.FONT.B3};
  color: ${({ theme }) => theme.COLOR.GREY_02};
`;

const BranchTag = styled.span`
  flex: 0 0 auto;
  ${({ theme }) => theme.FONT.B3};
  font-weight: 700;
  color: ${getPrimaryColor(JEJURO)};
  border: 1px solid ${getPrimaryColor(JEJURO)}33;
  background: ${getPrimaryColor(JEJURO)}0f;
  border-radius: 6px;
  padding: 2px 7px;
`;

const BranchName = styled.span`
  min-width: 0;
  word-break: keep-all;
`;

const Thumb = styled.div<{ isMobile?: boolean }>`
  position: relative;
  flex: 0 0 ${({ isMobile }) => (isMobile ? '160px' : '288px')};
  width: ${({ isMobile }) => (isMobile ? '160px' : '288px')};
  height: ${({ isMobile }) => (isMobile ? '116px' : '180px')};
  display: grid;
  place-items: center;
  border-radius: 12px;
  background: linear-gradient(
    150deg,
    rgba(0, 145, 232, 0.08),
    ${({ theme }) => theme.COLOR.GREY_BG}
  );
  overflow: hidden;

  img {
    width: 92%;
    height: 92%;
    object-fit: contain;
  }
`;

const SoldOut = styled.span`
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  background: rgba(15, 27, 36, 0.45);
  color: ${({ theme }) => theme.COLOR.WHITE};
  ${({ theme }) => theme.FONT.B3};
  font-weight: 800;
  z-index: 1;
`;

const Tags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const Tag = styled.span`
  ${({ theme }) => theme.FONT.B3};
  color: ${getPrimaryColor(JEJURO)};
  border: 1px solid ${getPrimaryColor(JEJURO)}33;
  background: ${getPrimaryColor(JEJURO)}0a;
  border-radius: 999px;
  padding: 3px 10px;
`;

const Bottom = styled.div<{ isMobile?: boolean }>`
  display: flex;
  align-items: ${({ isMobile }) => (isMobile ? 'stretch' : 'flex-end')};
  justify-content: space-between;
  gap: 16px;
  flex-direction: ${({ isMobile }) => (isMobile ? 'column' : 'row')};
`;

// 차량료 — 박스가 아닌 인라인 텍스트로 노출해 '예약하기' 버튼과 두 버튼처럼
// 보이던 이질감을 없앤다.
const Price = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const PriceLabel = styled.span`
  ${({ theme }) => theme.FONT.B3};
  color: ${({ theme }) => theme.COLOR.GREY_02};
`;

const PriceSub = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Strike = styled.span`
  ${({ theme }) => theme.FONT.B3};
  color: ${({ theme }) => theme.COLOR.GREY_03};
  text-decoration: line-through;
  font-variant-numeric: tabular-nums;
`;

const DiscountBadge = styled.span`
  ${({ theme }) => theme.FONT.B3};
  font-weight: 800;
  color: ${getPrimaryColor(JEJURO)};
`;

const PriceMain = styled.div`
  ${({ theme }) => theme.FONT.H2};
  font-weight: 800;
  letter-spacing: -0.02em;
  color: ${getPrimaryColor(JEJURO)};
  font-variant-numeric: tabular-nums;
`;

const PriceUnit = styled.span`
  ${({ theme }) => theme.FONT.B1};
  font-weight: 700;
  margin-left: 2px;
`;

const ReserveButton = styled.button`
  flex: 0 0 auto;
  min-width: 120px;
  height: 48px;
  padding: 0 22px;
  border: none;
  border-radius: 12px;
  background: ${getPrimaryColor(JEJURO)};
  color: ${({ theme }) => theme.COLOR.WHITE};
  ${({ theme }) => theme.FONT.B1};
  font-weight: 800;
  cursor: pointer;
  transition: filter 0.15s ease;

  &:hover {
    filter: brightness(0.96);
  }

  &:disabled {
    background: ${({ theme }) => theme.COLOR.GREY_04};
    cursor: not-allowed;
  }
`;
